import React, { useState } from 'react';
import { Type, Copy, Check, Trash2 } from 'lucide-react';

type CaseType = 'upper' | 'lower' | 'title' | 'sentence' | 'camel' | 'pascal' | 'snake' | 'kebab' | 'constant' | 'dot';

const TextCaseConverter: React.FC = () => {
  const [input, setInput] = useState('');
  const [copied, setCopied] = useState<CaseType | null>(null);

  const toWords = (text: string): string[] => {
    return text
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_\-.]+/g, ' ')
      .toLowerCase()
      .split(/\s+/)
      .filter(w => w.length > 0);
  };

  const conversions: Record<CaseType, { label: string; convert: (text: string) => string; example: string }> = {
    upper: {
      label: 'UPPERCASE',
      convert: (t) => t.toUpperCase(),
      example: 'HELLO WORLD',
    },
    lower: {
      label: 'lowercase',
      convert: (t) => t.toLowerCase(),
      example: 'hello world',
    },
    title: {
      label: 'Title Case',
      convert: (t) => t.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()),
      example: 'Hello World',
    },
    sentence: {
      label: 'Sentence case',
      convert: (t) => {
        const lower = t.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      },
      example: 'Hello world',
    },
    camel: {
      label: 'camelCase',
      convert: (t) => {
        const words = toWords(t);
        return words.map((w, i) => i === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1)).join('');
      },
      example: 'helloWorld',
    },
    pascal: {
      label: 'PascalCase',
      convert: (t) => {
        const words = toWords(t);
        return words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('');
      },
      example: 'HelloWorld',
    },
    snake: {
      label: 'snake_case',
      convert: (t) => toWords(t).join('_'),
      example: 'hello_world',
    },
    kebab: {
      label: 'kebab-case',
      convert: (t) => toWords(t).join('-'),
      example: 'hello-world',
    },
    constant: {
      label: 'CONSTANT_CASE',
      convert: (t) => toWords(t).join('_').toUpperCase(),
      example: 'HELLO_WORLD',
    },
    dot: {
      label: 'dot.case',
      convert: (t) => toWords(t).join('.'),
      example: 'hello.world',
    },
  };

  const copyToClipboard = async (caseType: CaseType) => {
    const converted = conversions[caseType].convert(input);
    await navigator.clipboard.writeText(converted);
    setCopied(caseType);
    setTimeout(() => setCopied(null), 2000);
  };

  const stats = {
    characters: input.length,
    words: input.trim() ? input.trim().split(/\s+/).length : 0,
    lines: input ? input.split('\n').length : 0,
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Text Case Converter</h2>
        <p className="text-gray-600">Convert text between different case formats</p>
      </div>

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Input */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-gray-700">Input Text</label>
            <button
              onClick={() => setInput('')}
              className="p-1 text-gray-500 hover:text-gray-700"
              disabled={!input}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="w-full h-32 px-4 py-3 text-lg border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            placeholder="Enter your text here..."
          />
          <div className="flex gap-4 mt-2 text-sm text-gray-500">
            <span>{stats.characters} characters</span>
            <span>{stats.words} words</span>
            <span>{stats.lines} lines</span>
          </div>
        </div>

        {/* Conversions Grid */}
        <div className="grid md:grid-cols-2 gap-3">
          {(Object.entries(conversions) as [CaseType, typeof conversions[CaseType]][]).map(([key, { label, convert, example }]) => (
            <div
              key={key}
              className="bg-white rounded-xl border border-gray-200 p-4 hover:border-pink-300 transition-colors"
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <p className="text-xs text-gray-400">e.g., {example}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(key)}
                  className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={!input}
                >
                  {copied === key ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-600" />
                  )}
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm break-all min-h-[60px]">
                {input ? convert(input) : <span className="text-gray-400">Output will appear here</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Info */}
        <div className="bg-pink-50 rounded-xl p-4 border border-pink-100">
          <div className="flex items-start gap-3">
            <Type className="w-5 h-5 text-pink-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-pink-900 mb-2">Case Formats</h4>
              <div className="text-sm text-pink-700 grid grid-cols-2 gap-x-4">
                <p><strong>camelCase:</strong> JavaScript variables</p>
                <p><strong>PascalCase:</strong> Class names</p>
                <p><strong>snake_case:</strong> Python, databases</p>
                <p><strong>kebab-case:</strong> URLs, CSS classes</p>
                <p><strong>CONSTANT_CASE:</strong> Constants</p>
                <p><strong>dot.case:</strong> File extensions, configs</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextCaseConverter;
